import { useState, useEffect } from 'react';
import {
  Stack,
  Group,
  Text,
  Button,
  Card,
  Badge,
  SimpleGrid,
  Modal,
  Drawer,
  TextInput,
  Textarea,
  NumberInput,
  Switch,
  Alert,
  Loader,
  Center,
  ActionIcon,
  Tooltip,
  Box,
  Divider,
  Pagination,
  ScrollArea,
  UnstyledButton,
} from '@mantine/core';
import {
  IconPlus,
  IconTrash,
  IconX,
  IconExclamationCircle,
  IconAlbum,
  IconPhoto,
  IconVideo,
  IconCoin,
  IconEyeOff,
} from '@tabler/icons-react';
import { useApi, useApiClient, mediaUrl } from '../../api';
import { formatCents } from '../products/productDisplay';
import ProtectedMediaItem from '../media/ProtectedMediaItem';

// Priced album management (contract §3.15 Album + §4.16 owner CRUD under
// /api/v1/me/seller/albums). Albums group seller media behind a one-time
// pay-per-view unlock.

const PAGE_LIMIT = 12;
const GALLERY_PAGE_LIMIT = 100;
// price_cents 100–50000 (contract §4.16)
const MIN_DOLLARS = 1;
const MAX_DOLLARS = 500;
const PRICE_MESSAGE = `Price must be between $${MIN_DOLLARS} and $${MAX_DOLLARS}`;

// Owner seller-media item is published when publish_at is null or past (§5)
const isPublishedItem = (item) => !item.publish_at || new Date(item.publish_at) <= new Date();
// Items that public /media will not serve → render via the protected route
const isProtectedItem = (item) => !item.is_public || !isPublishedItem(item);

const mediaLabel = (item) => item.title || item.original_filename || (item.media_type === 'video' ? 'Video' : 'Photo');

// Static tile for videos inside the editor — thumbnails never fetch video
// bytes (the protected route is a full no-store download).
const VideoTile = ({ item }) => (
  <Center h="100%">
    <Stack align="center" gap={2}>
      <IconVideo size="1.4rem" color="var(--mantine-color-gray-4)" />
      <Text size="xs" c="gray.5" px={4} lineClamp={1}>{mediaLabel(item)}</Text>
    </Stack>
  </Center>
);

const MediaThumb = ({ item, height = 100 }) => (
  <Box style={{ position: 'relative', height, borderRadius: 6, overflow: 'hidden', background: '#111' }}>
    {item.media_type === 'video' ? (
      <VideoTile item={item} />
    ) : (
      <ProtectedMediaItem
        mediaId={item.id}
        mediaType="image"
        title={item.title}
        alt={mediaLabel(item)}
        filePath={item.file_path}
        isProtected={isProtectedItem(item)}
      />
    )}
    {!item.is_public && (
      <Badge size="xs" variant="filled" color="grape" style={{ position: 'absolute', top: 4, left: 4 }}>
        <IconEyeOff size="0.65rem" />
      </Badge>
    )}
  </Box>
);

// ── Create modal ─────────────────────────────────────────────────────────────

const CreateAlbumModal = ({ opened, onClose, onCreated }) => {
  const api = useApiClient();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dollars, setDollars] = useState(15);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const amount = typeof dollars === 'number' ? dollars : parseFloat(dollars);
  const priceValid = Number.isFinite(amount) && amount >= MIN_DOLLARS && amount <= MAX_DOLLARS;
  const valid = title.trim().length > 0 && priceValid;

  const reset = () => {
    setTitle('');
    setDescription('');
    setDollars(15);
    setError(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleCreate = async () => {
    if (!valid || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      // Money is integer cents at the API boundary (contract §1.4)
      const body = { title: title.trim(), price_cents: Math.round(amount * 100) };
      if (description.trim()) body.description = description.trim();
      const album = await api.post('/api/v1/me/seller/albums', body);
      reset();
      onCreated(album);
    } catch (err) {
      setError(err?.message || 'Could not create the album');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal opened={opened} onClose={handleClose} title="Create album" centered transitionProps={{ duration: 0 }}>
      <Stack gap="md">
        <TextInput
          label="Title"
          placeholder="Beach set"
          value={title}
          onChange={(e) => setTitle(e.currentTarget.value)}
          maxLength={200}
          required
        />
        <Textarea
          label="Description"
          placeholder="Optional description"
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
          maxLength={2000}
          autosize
          minRows={2}
        />
        <NumberInput
          label="Price (USD)"
          value={dollars}
          onChange={setDollars}
          min={MIN_DOLLARS}
          max={MAX_DOLLARS}
          decimalScale={2}
          prefix="$"
          error={!priceValid && dollars !== '' ? PRICE_MESSAGE : null}
        />
        {error && (
          <Alert icon={<IconExclamationCircle size="1rem" />} color="red" variant="light">{error}</Alert>
        )}
        <Text size="xs" c="dimmed">
          Albums start as drafts — add items and a cover, then publish when it's ready.
        </Text>
        <Group justify="flex-end" gap="sm">
          <Button variant="light" color="gray" onClick={handleClose} disabled={submitting}>Cancel</Button>
          <Button color="grape" onClick={handleCreate} disabled={!valid} loading={submitting}>Create</Button>
        </Group>
      </Stack>
    </Modal>
  );
};

// ── Details form (title / description / price via PATCH) ─────────────────────

const AlbumDetailsForm = ({ album, busy, onSave }) => {
  const [title, setTitle] = useState(album.title);
  const [description, setDescription] = useState(album.description || '');
  const [dollars, setDollars] = useState(album.price_cents / 100);

  const amount = typeof dollars === 'number' ? dollars : parseFloat(dollars);
  const priceValid = Number.isFinite(amount) && amount >= MIN_DOLLARS && amount <= MAX_DOLLARS;
  const valid = title.trim().length > 0 && priceValid;

  return (
    <Stack gap="xs">
      <Group grow align="flex-start">
        <TextInput
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.currentTarget.value)}
          maxLength={200}
          required
        />
        <NumberInput
          label="Price (USD)"
          description="Changes never affect past purchases"
          value={dollars}
          onChange={setDollars}
          min={MIN_DOLLARS}
          max={MAX_DOLLARS}
          decimalScale={2}
          prefix="$"
          error={!priceValid && dollars !== '' ? PRICE_MESSAGE : null}
        />
      </Group>
      <Textarea
        label="Description"
        value={description}
        onChange={(e) => setDescription(e.currentTarget.value)}
        maxLength={1000}
        autosize
        minRows={2}
      />
      <Button
        color="grape"
        variant="light"
        style={{ alignSelf: 'flex-start' }}
        disabled={!valid}
        loading={busy}
        onClick={() =>
          onSave({
            title: title.trim(),
            description: description.trim() || null,
            price_cents: Math.round(amount * 100),
          })
        }
      >
        Save details
      </Button>
    </Stack>
  );
};

// ── Album editor (drawer body) ───────────────────────────────────────────────

const AlbumEditor = ({ albumId, onClose, onChanged }) => {
  const api = useApiClient();
  const { data: album, loading, error: loadError, refetch } = useApi(`/api/v1/me/seller/albums/${albumId}`);

  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteBlocked, setDeleteBlocked] = useState(false);

  // Owner gallery for the add-items picker + cover choices. The gallery is
  // paginated (max limit 100) and can exceed one page, so pages are fetched
  // incrementally and appended ("Load more" while total > loaded).
  const [gallery, setGallery] = useState({ items: [], total: null, page: 0 });
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [galleryError, setGalleryError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setGallery({ items: [], total: null, page: 0 });
    setGalleryError(null);
    setGalleryLoading(true);
    api.get('/api/v1/me/seller/media', { page: 1, limit: GALLERY_PAGE_LIMIT })
      .then((res) => {
        if (!cancelled) setGallery({ items: res.items, total: res.total, page: 1 });
      })
      .catch((err) => {
        if (!cancelled) setGalleryError(err?.message || 'Could not load your gallery');
      })
      .finally(() => {
        if (!cancelled) setGalleryLoading(false);
      });
    return () => { cancelled = true; };
  }, [api, albumId]);

  const loadMoreGallery = async () => {
    if (galleryLoading) return;
    setGalleryError(null);
    setGalleryLoading(true);
    try {
      const next = gallery.page + 1;
      const res = await api.get('/api/v1/me/seller/media', { page: next, limit: GALLERY_PAGE_LIMIT });
      setGallery((prev) => ({
        // Dedupe by id in case items shifted between page fetches
        items: [...prev.items, ...res.items.filter((m) => !prev.items.some((p) => p.id === m.id))],
        total: res.total,
        page: next,
      }));
    } catch (err) {
      setGalleryError(err?.message || 'Could not load more of your gallery');
    } finally {
      setGalleryLoading(false);
    }
  };

  if (loading) return <Center py="xl"><Loader color="grape" /></Center>;
  if (loadError || !album) {
    return (
      <Alert icon={<IconExclamationCircle size="1rem" />} color="red">
        {loadError?.message || 'Album not found'}
      </Alert>
    );
  }

  const items = album.items ?? [];
  const itemIds = new Set(items.map((i) => i.id));
  const allMedia = gallery.items;
  const hasMoreGallery = gallery.total != null && allMedia.length < gallery.total;
  const addable = allMedia.filter((m) => !itemIds.has(m.id));
  // Cover must be a public + published item of the seller (contract §4.16);
  // covers render as <img> everywhere, so offer images only.
  const coverCandidates = allMedia.filter(
    (m) => m.media_type === 'image' && m.is_public && isPublishedItem(m),
  );

  const mutate = async (fn) => {
    setActionError(null);
    setBusy(true);
    try {
      await fn();
      await refetch();
      onChanged?.();
    } catch (err) {
      // Surface problem detail (e.g. the 422 "needs ≥1 item and a cover")
      setActionError(err?.message || 'Something went wrong');
    } finally {
      setBusy(false);
    }
  };

  const addItem = (mediaId) => mutate(() => api.put(`/api/v1/me/seller/albums/${albumId}/items/${mediaId}`, {}));
  const removeItem = (mediaId) => mutate(() => api.del(`/api/v1/me/seller/albums/${albumId}/items/${mediaId}`));
  const setCover = (mediaId) => mutate(() => api.patch(`/api/v1/me/seller/albums/${albumId}`, { cover_media_id: mediaId }));
  const saveDetails = (body) => mutate(() => api.patch(`/api/v1/me/seller/albums/${albumId}`, body));
  const togglePublish = (checked) => mutate(() => api.patch(`/api/v1/me/seller/albums/${albumId}`, { is_published: checked }));

  const handleDelete = async () => {
    setConfirmDelete(false);
    setActionError(null);
    try {
      await api.del(`/api/v1/me/seller/albums/${albumId}`);
      onChanged?.();
      onClose();
    } catch (err) {
      if (err?.code === 'conflict') {
        // Completed purchases exist — buyers keep what they paid for (§4.16)
        setDeleteBlocked(true);
      } else {
        setActionError(err?.message || 'Could not delete the album');
      }
    }
  };

  return (
    <Stack gap="lg">
      {actionError && (
        <Alert icon={<IconExclamationCircle size="1rem" />} color="red" variant="light">
          {actionError}
        </Alert>
      )}
      {deleteBlocked && (
        <Alert icon={<IconExclamationCircle size="1rem" />} color="orange" variant="light" title="This album has been purchased">
          Buyers keep access to what they paid for, so purchased albums can't be deleted.
          Unpublish it instead to hide it from your page.
        </Alert>
      )}

      {/* Publish state */}
      <Group justify="space-between" align="center" wrap="wrap" gap="md">
        <Switch
          color="green"
          checked={album.is_published}
          onChange={(e) => togglePublish(e.currentTarget.checked)}
          disabled={busy}
          label={album.is_published ? 'Published — visible on your page' : 'Draft — hidden from buyers'}
          description="Publishing requires at least one item and a cover"
        />
        <Badge color="grape" variant="light" leftSection={<IconCoin size="0.7rem" />}>
          {album.purchase_count} sold
        </Badge>
      </Group>

      <Divider />

      <AlbumDetailsForm key={`${album.id}-${album.updated_at}`} album={album} busy={busy} onSave={saveDetails} />

      <Divider />

      {/* Current items */}
      <Stack gap="xs">
        <Text fw={600} size="sm">Items in this album ({items.length})</Text>
        {items.length === 0 ? (
          <Text size="xs" c="dimmed">No items yet — add some from your gallery below.</Text>
        ) : (
          <SimpleGrid cols={{ base: 3, sm: 4 }} spacing="xs">
            {items.map((item) => (
              <Card key={item.id} padding={4} radius="md" withBorder>
                <Box style={{ position: 'relative' }}>
                  <MediaThumb item={item} />
                  {album.cover_media_id === item.id && (
                    <Badge size="xs" color="yellow" variant="filled" style={{ position: 'absolute', bottom: 4, left: 4 }}>
                      Cover
                    </Badge>
                  )}
                </Box>
                <Group justify="flex-end" mt={4}>
                  <Tooltip label="Remove from album">
                    <ActionIcon
                      size="xs"
                      variant="subtle"
                      color="red"
                      aria-label={`Remove ${mediaLabel(item)} from album`}
                      onClick={() => removeItem(item.id)}
                      disabled={busy}
                    >
                      <IconX size="0.8rem" />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              </Card>
            ))}
          </SimpleGrid>
        )}
      </Stack>

      {/* Add items */}
      <Stack gap="xs">
        <Text fw={600} size="sm">Add from your gallery</Text>
        {galleryError && (
          <Alert icon={<IconExclamationCircle size="1rem" />} color="red" variant="light">
            {galleryError}
          </Alert>
        )}
        {addable.length === 0 ? (
          <Text size="xs" c="dimmed">
            {galleryLoading
              ? 'Loading your gallery…'
              : allMedia.length === 0
                ? 'Your gallery is empty — upload media first.'
                : 'All of your gallery media is already in this album.'}
          </Text>
        ) : (
          <SimpleGrid cols={{ base: 3, sm: 4 }} spacing="xs">
            {addable.map((item) => (
              <Card key={item.id} padding={4} radius="md" withBorder>
                <MediaThumb item={item} height={90} />
                <Button
                  size="compact-xs"
                  variant="light"
                  color="grape"
                  fullWidth
                  mt={4}
                  leftSection={<IconPlus size="0.7rem" />}
                  onClick={() => addItem(item.id)}
                  disabled={busy}
                  aria-label={`Add ${mediaLabel(item)} to album`}
                >
                  Add
                </Button>
              </Card>
            ))}
          </SimpleGrid>
        )}
        {hasMoreGallery && (
          <Button
            variant="subtle"
            color="grape"
            size="xs"
            style={{ alignSelf: 'flex-start' }}
            onClick={loadMoreGallery}
            loading={galleryLoading}
          >
            Load more ({allMedia.length} of {gallery.total})
          </Button>
        )}
      </Stack>

      <Divider />

      {/* Cover selector — public + published images only (contract §4.16) */}
      <Stack gap="xs">
        <Text fw={600} size="sm">Cover</Text>
        <Text size="xs" c="dimmed">
          The cover is shown to everyone, so it must be a public, published photo — private album content stays hidden.
        </Text>
        {coverCandidates.length === 0 ? (
          <Text size="xs" c="dimmed">No public photos available — make one of your gallery photos public first.</Text>
        ) : (
          <ScrollArea type="auto">
            <Group gap="xs" wrap="nowrap" py={4}>
              {coverCandidates.map((m) => (
                <UnstyledButton
                  key={m.id}
                  onClick={() => setCover(m.id)}
                  disabled={busy}
                  aria-label={`Use ${mediaLabel(m)} as cover`}
                  style={{
                    border: album.cover_media_id === m.id
                      ? '2px solid var(--mantine-color-grape-6)'
                      : '2px solid transparent',
                    borderRadius: 8,
                    overflow: 'hidden',
                    flexShrink: 0,
                    lineHeight: 0,
                  }}
                >
                  <img
                    src={mediaUrl(m.file_path)}
                    alt={mediaLabel(m)}
                    style={{ width: 72, height: 72, objectFit: 'cover', display: 'block' }}
                  />
                </UnstyledButton>
              ))}
            </Group>
          </ScrollArea>
        )}
      </Stack>

      <Divider />

      {/* Delete */}
      <Group justify="flex-end">
        <Button
          variant="light"
          color="red"
          leftSection={<IconTrash size="1rem" />}
          onClick={() => setConfirmDelete(true)}
          disabled={busy}
        >
          Delete album
        </Button>
      </Group>

      <Modal
        opened={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title="Delete this album?"
        centered
        transitionProps={{ duration: 0 }}
      >
        <Stack gap="md">
          <Text size="sm">
            "{album.title}" will be deleted. Your media files stay in your gallery — only the album is removed.
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button variant="light" color="gray" onClick={() => setConfirmDelete(false)}>Cancel</Button>
            <Button color="red" onClick={handleDelete}>Delete</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
};

// ── Main component ───────────────────────────────────────────────────────────

const SellerAlbums = () => {
  const [page, setPage] = useState(1);
  const { data, loading, error, refetch } = useApi('/api/v1/me/seller/albums', { page, limit: PAGE_LIMIT });
  const [createOpen, setCreateOpen] = useState(false);
  const [editorId, setEditorId] = useState(null);

  const albums = data?.items ?? [];
  const totalPages = Math.max(1, Math.ceil((data?.total ?? 0) / PAGE_LIMIT));

  if (loading) {
    return <Center py="xl"><Loader color="grape" /></Center>;
  }

  return (
    <Stack gap="lg">
      {error && (
        <Alert icon={<IconExclamationCircle size="1rem" />} color="red">
          Failed to load albums: {error.message}
        </Alert>
      )}

      <Group justify="space-between" align="center">
        <Text size="sm" c="dimmed">
          Group photos and videos into a priced album — buyers pay once to unlock it forever.
        </Text>
        <Button color="grape" leftSection={<IconPlus size="1rem" />} onClick={() => setCreateOpen(true)}>
          Create album
        </Button>
      </Group>

      {albums.length === 0 ? (
        <Center py="xl">
          <Stack align="center" gap="sm">
            <IconAlbum size="3rem" color="var(--mantine-color-gray-4)" />
            <Text c="dimmed">No albums yet — create one to start selling unlocks!</Text>
          </Stack>
        </Center>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
          {albums.map((album) => (
            <Card key={album.id} shadow="sm" padding="md" radius="md" withBorder data-testid={`seller-album-${album.id}`}>
              <Stack gap="xs">
                <Group justify="space-between" wrap="nowrap">
                  <Text fw={600} lineClamp={1}>{album.title}</Text>
                  <Badge color={album.is_published ? 'green' : 'gray'} variant="light" style={{ flexShrink: 0 }}>
                    {album.is_published ? 'Published' : 'Draft'}
                  </Badge>
                </Group>
                <Group gap="lg">
                  <Text size="lg" fw={700} c="pink">{formatCents(album.price_cents)}</Text>
                  <Group gap={4}>
                    <IconPhoto size="0.85rem" color="var(--mantine-color-gray-6)" />
                    <Text size="sm" c="dimmed">
                      {album.item_count} item{album.item_count === 1 ? '' : 's'}
                    </Text>
                  </Group>
                  <Group gap={4}>
                    <IconCoin size="0.85rem" color="var(--mantine-color-gray-6)" />
                    <Text size="sm" c="dimmed">{album.purchase_count} sold</Text>
                  </Group>
                </Group>
                <Button variant="light" color="grape" onClick={() => setEditorId(album.id)}>
                  Manage
                </Button>
              </Stack>
            </Card>
          ))}
        </SimpleGrid>
      )}

      {totalPages > 1 && (
        <Group justify="center">
          <Pagination value={page} onChange={setPage} total={totalPages} color="grape" />
        </Group>
      )}

      <CreateAlbumModal
        opened={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(album) => {
          setCreateOpen(false);
          refetch();
          setEditorId(album.id); // jump straight into adding items
        }}
      />

      <Drawer
        opened={editorId != null}
        onClose={() => setEditorId(null)}
        title="Manage album"
        position="right"
        size="xl"
        transitionProps={{ duration: 0 }}
      >
        {editorId != null && (
          <AlbumEditor albumId={editorId} onClose={() => setEditorId(null)} onChanged={refetch} />
        )}
      </Drawer>
    </Stack>
  );
};

export default SellerAlbums;
